import React, { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  MappedLeadItem,
  useGetDealerLeads,
} from "../../queries/useGetDealerLeads";
import {
  DEALER_DATE_TIME,
  DateDisplay,
} from "../../ui/DateDisplay/DateDisplay";
import { Placeholder } from "../../ui/Placeholder";
import { TableHeader } from "../ColumnSort/TableHeader";
import { NoData } from "../NoData/NoData";

const urlPattern = /(https?:\/\/\S+)/;

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch (_e) {
    return url;
  }
};

const linkifyText = (
  text: string,
  t: (key: string) => string,
): ReactNode => {
  const parts = text.split(urlPattern);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    urlPattern.test(part) ? (
      <a className="text-primary underline" href={part} key={i} rel="noreferrer" target="_blank">
        {part.includes("#compare-tires")
          ? `${t("leadReport.viewComparison")} (${getHostname(part)})`
          : part}
      </a>
    ) : (
      part
    ),
  );
};

const placehoders: MappedLeadItem[] = [];

for (let i = 0; i < 3; i++) {
  placehoders.push({} as MappedLeadItem);
}

export const LeadsTable: FunctionComponent = () => {
  const { t } = useTranslation();
  const { data = placehoders, isLoading } = useGetDealerLeads();
  return (
    <>
      <table className="border-collapse table-auto w-full text-xs">
        <thead>
          <tr className="border-b-2 border-edge align-bottom">
            <TableHeader
              className="text-left"
              field="ownerType"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="generatedByActionType"
              tableName="leadReport"
            />
            <TableHeader
              className="text-right"
              field="orderId"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="status"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="customerName"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="customerEmail"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="customerPhone"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="requestedDate"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="createdDate"
              tableName="leadReport"
            />
            <TableHeader
              className="text-left"
              field="customerNotes"
              tableName="leadReport"
            />
          </tr>
        </thead>
        <tbody>
          {data.map(
            (
              {
                appointmentRequestTime,
                createdDate,
                customerEmail,
                customerName,
                customerNotes,
                customerPhone,
                generatedByActionType,
                leadId,
                orderId,
                ownerType,
                requestedDate,
                status,
              },
              key,
            ) => (
              <tr
                className="border-b border-edge align-top"
                key={`${leadId}-${key}`}
              >
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>{ownerType}</Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>
                    {generatedByActionType}
                  </Placeholder>
                </td>
                <td className="px-2 py-1 text-right">
                  <Placeholder loading={isLoading}>{orderId}</Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>{status}</Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>{customerName}</Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>{customerEmail}</Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>{customerPhone}</Placeholder>
                </td>
                <td className="px-2 py-1 min-w-[8em]">
                  <Placeholder loading={isLoading}>
                    <DateDisplay format={DEALER_DATE_TIME}>
                      {requestedDate}
                    </DateDisplay>{" "}
                    {appointmentRequestTime}
                  </Placeholder>
                </td>
                <td className="px-2 py-1 min-w-[8em]">
                  <Placeholder loading={isLoading}>
                    <DateDisplay format={DEALER_DATE_TIME}>
                      {createdDate}
                    </DateDisplay>
                  </Placeholder>
                </td>
                <td className="px-2 py-1">
                  <Placeholder loading={isLoading}>
                    {customerNotes ? linkifyText(customerNotes, t) : customerNotes}
                  </Placeholder>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      {data.length === 0 && (
        <NoData className="max-w-[12em] mx-auto my-8">
          {t("quoteReport.noData")}
        </NoData>
      )}
    </>
  );
};

/**
 * @see docs/worklog/TW-874-comparison-lead-link/rfc.md
 * @see docs/worklog/TW-874-comparison-lead-link/tfc.md
 */
